import React, { useEffect, useState } from 'react';
import { Card, Row, Col, Statistic, Table, message, Select, DatePicker, Button, Tag, Space, Divider } from 'antd';
import {
    UserOutlined,
    TeamOutlined,
    MedicineBoxOutlined,
    FileTextOutlined,
    ShopOutlined,
    BarChartOutlined,
    DownloadOutlined,
    ReloadOutlined
} from '@ant-design/icons';
import { superAdminService } from '../../services';
import { useSearchParams } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import dayjs from 'dayjs';
import './Reports.css';

const { RangePicker } = DatePicker;
const { Option } = Select;

const SuperAdminReports = () => {
    const [loading, setLoading] = useState(false);
    const [overviewData, setOverviewData] = useState(null);
    const [doctors, setDoctors] = useState([]);
    const [selectedDoctor, setSelectedDoctor] = useState(null);
    const [doctorReport, setDoctorReport] = useState(null);
    const [dateRange, setDateRange] = useState(null);
    const [searchParams] = useSearchParams();
    const { t } = useTranslation();

    useEffect(() => {
        fetchOverviewReport();
        fetchDoctors();
    }, []);

    const fetchOverviewReport = async () => {
        setLoading(true);
        try {
            const response = await superAdminService.getOverviewReport();
            console.log('Overview report response:', response);
            // Handle both response.data and response.data.data
            const data = response.data?.data || response.data;
            console.log('Processed overview data:', data);
            setOverviewData(data);
        } catch (error) {
            console.error('Error fetching overview:', error);
            message.error(error.response?.data?.message || t('error'));
        } finally {
            setLoading(false);
        }
    };

    const fetchDoctors = async () => {
        try {
            const response = await superAdminService.getDoctors();
            console.log('Doctors response:', response);
            // Handle both response.data and response.data.data
            const doctorsList = response.data?.data || response.data || [];
            console.log('Processed doctors:', doctorsList);
            setDoctors(doctorsList);
        } catch (error) {
            console.error('Error fetching doctors:', error);
        }
    };

    const fetchDoctorReport = async (doctorId) => {
        if (!doctorId) return;

        setLoading(true);
        try {
            const response = await superAdminService.getDoctorReport(doctorId);
            console.log('Doctor report response:', response);
            // Handle both response.data and response.data.data
            const data = response.data?.data || response.data;
            console.log('Processed doctor report:', data);
            setDoctorReport(data);
        } catch (error) {
            console.error('Error fetching doctor report:', error);
            message.error(error.response?.data?.message || t('error'));
        } finally {
            setLoading(false);
        }
    };

    const handleDoctorChange = (doctorId) => {
        setSelectedDoctor(doctorId);
        if (doctorId) {
            fetchDoctorReport(doctorId);
        } else {
            setDoctorReport(null);
        }
    };

    // Pre-select a doctor when navigated here with ?doctor=<id> (e.g. from the Doctors page)
    useEffect(() => {
        const doctorId = searchParams.get('doctor');
        if (doctorId) {
            handleDoctorChange(doctorId);
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [searchParams]);

    const handleExportReport = () => {
        try {
            if (!overviewData && !doctorReport) {
                message.warning(t('noDataToExport') || 'No data to export');
                return;
            }

            let csvContent = '';
            const timestamp = dayjs().format('YYYY-MM-DD_HH-mm-ss');
            let filename = '';

            if (doctorReport) {
                // Export doctor-specific report
                filename = `Doctor_Report_${doctorReport.doctor?.name}_${timestamp}.csv`;

                // Doctor Information
                csvContent += `Doctor Report\n`;
                csvContent += `Generated: ${dayjs().format('YYYY-MM-DD HH:mm:ss')}\n\n`;
                csvContent += `Doctor Information\n`;
                csvContent += `Name,${doctorReport.doctor?.name}\n`;
                csvContent += `Specialization,${doctorReport.doctor?.specialization}\n`;
                csvContent += `Email,${doctorReport.doctor?.email}\n`;
                csvContent += `Phone,${doctorReport.doctor?.phoneNumber}\n`;
                csvContent += `Status,${doctorReport.doctor?.isActive ? 'Active' : 'Inactive'}\n\n`;

                // Statistics
                csvContent += `Statistics\n`;
                csvContent += `Total Patients,${doctorReport.statistics?.totalPatients || 0}\n`;
                csvContent += `Total Visits,${doctorReport.statistics?.totalVisits || 0}\n`;
                csvContent += `Total Secretaries,${doctorReport.statistics?.totalSecretaries || 0}\n`;
                csvContent += `Total Drugs Prescribed,${doctorReport.statistics?.totalDrugsPrescribed || 0}\n\n`;

                // Top Prescribed Drugs
                if (doctorReport.topPrescribedDrugs && doctorReport.topPrescribedDrugs.length > 0) {
                    csvContent += `Top Prescribed Drugs\n`;
                    csvContent += `Drug Name,Prescription Count\n`;
                    doctorReport.topPrescribedDrugs.forEach(drug => {
                        csvContent += `${drug.name},${drug.count}\n`;
                    });
                    csvContent += `\n`;
                }

                // Patients List
                if (doctorReport.patients && doctorReport.patients.length > 0) {
                    csvContent += `Patients List\n`;
                    csvContent += `Name,Phone Number,Gender,Registration Date\n`;
                    doctorReport.patients.forEach(patient => {
                        csvContent += `${patient.name},${patient.phoneNumber},${patient.gender || '-'},${dayjs(patient.createdAt).format('YYYY-MM-DD')}\n`;
                    });
                }
            } else {
                // Export system overview
                filename = `System_Overview_Report_${timestamp}.csv`;

                csvContent += `System Overview Report\n`;
                csvContent += `Generated: ${dayjs().format('YYYY-MM-DD HH:mm:ss')}\n\n`;
                csvContent += `Statistics\n`;
                csvContent += `Metric,Count\n`;
                csvContent += `Total Doctors,${overviewData?.totalDoctors || 0}\n`;
                csvContent += `Total Secretaries,${overviewData?.totalSecretaries || 0}\n`;
                csvContent += `Total Medical Sellers,${overviewData?.totalMedicalSellers || 0}\n`;
                csvContent += `Total Patients,${overviewData?.totalPatients || 0}\n`;
                csvContent += `Total Visits,${overviewData?.totalVisits || 0}\n`;
                csvContent += `Total Drugs Prescribed,${overviewData?.totalDrugsPrescribed || 0}\n`;
            }

            // Create and download CSV file
            const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
            const link = document.createElement('a');
            const url = URL.createObjectURL(blob);

            link.setAttribute('href', url);
            link.setAttribute('download', filename);
            link.style.visibility = 'hidden';
            document.body.appendChild(link);
            link.click();
            document.body.removeChild(link);

            message.success(t('reportExported') || 'Report exported successfully');
        } catch (error) {
            console.error('Export error:', error);
            message.error(t('exportFailed') || 'Failed to export report');
        }
    };

    const topDrugsColumns = [
        {
            title: t('drugName'),
            dataIndex: 'name',
            key: 'name',
            render: (text) => <strong>{text}</strong>
        },
        {
            title: t('prescriptionCount'),
            dataIndex: 'count',
            key: 'count',
            sorter: (a, b) => a.count - b.count,
            render: (count) => <Tag color="blue">{count}</Tag>
        }
    ];

    const patientsColumns = [
        {
            title: t('patientName'),
            dataIndex: 'name',
            key: 'name'
        },
        {
            title: t('phoneNumber'),
            dataIndex: 'phoneNumber',
            key: 'phoneNumber'
        },
        {
            title: t('gender'),
            dataIndex: 'gender',
            key: 'gender',
            render: (gender) => gender ? t(gender) : '-'
        },
        {
            title: t('registrationDate'),
            dataIndex: 'createdAt',
            key: 'createdAt',
            render: (date) => dayjs(date).format('YYYY-MM-DD')
        }
    ];

    const recentVisitsColumns = [
        {
            title: t('patient'),
            dataIndex: ['patientId', 'name'],
            key: 'patient',
            render: (text, record) => record.patientId?.name || '-'
        },
        {
            title: t('phoneNumber'),
            dataIndex: ['patientId', 'phoneNumber'],
            key: 'phone',
            render: (text, record) => record.patientId?.phoneNumber || '-'
        },
        {
            title: t('visitDate'),
            dataIndex: 'createdAt',
            key: 'createdAt',
            render: (date) => dayjs(date).format('YYYY-MM-DD HH:mm')
        },
        {
            title: t('createdBy'),
            dataIndex: ['createdBy', 'name'],
            key: 'createdBy',
            render: (text, record) => (
                <Space>
                    <span>{record.createdBy?.name || '-'}</span>
                    <Tag color={record.createdBy?.role === 'doctor' ? 'green' : 'blue'}>
                        {record.createdBy?.role ? t(record.createdBy.role) : '-'}
                    </Tag>
                </Space>
            )
        }
    ];

    return (
        <div className="reports-container">
            <div className="reports-header">
                <h1>{t('reports')}</h1>
                <Space>
                    <Button
                        icon={<ReloadOutlined />}
                        onClick={fetchOverviewReport}
                        loading={loading}
                    >
                        {t('refresh')}
                    </Button>
                    <Button
                        type="primary"
                        icon={<DownloadOutlined />}
                        onClick={handleExportReport}
                    >
                        {t('exportReport')}
                    </Button>
                </Space>
            </div>

            {/* Overview Statistics */}
            <Card title={t('systemOverview')} className="overview-card" loading={loading}>
                <Row gutter={[16, 16]}>
                    <Col xs={24} sm={12} lg={8}>
                        <Card>
                            <Statistic
                                title={t('totalDoctors')}
                                value={overviewData?.totalDoctors || 0}
                                prefix={<UserOutlined />}
                                valueStyle={{ color: '#3f8600' }}
                            />
                        </Card>
                    </Col>
                    <Col xs={24} sm={12} lg={8}>
                        <Card>
                            <Statistic
                                title={t('totalSecretaries')}
                                value={overviewData?.totalSecretaries || 0}
                                prefix={<TeamOutlined />}
                                valueStyle={{ color: '#1890ff' }}
                            />
                        </Card>
                    </Col>
                    <Col xs={24} sm={12} lg={8}>
                        <Card>
                            <Statistic
                                title={t('totalMedicalSellers')}
                                value={overviewData?.totalMedicalSellers || 0}
                                prefix={<ShopOutlined />}
                                valueStyle={{ color: '#722ed1' }}
                            />
                        </Card>
                    </Col>
                    <Col xs={24} sm={12} lg={8}>
                        <Card>
                            <Statistic
                                title={t('totalPatients')}
                                value={overviewData?.totalPatients || 0}
                                prefix={<MedicineBoxOutlined />}
                                valueStyle={{ color: '#cf1322' }}
                            />
                        </Card>
                    </Col>
                    <Col xs={24} sm={12} lg={8}>
                        <Card>
                            <Statistic
                                title={t('totalVisits')}
                                value={overviewData?.totalVisits || 0}
                                prefix={<FileTextOutlined />}
                                valueStyle={{ color: '#fa8c16' }}
                            />
                        </Card>
                    </Col>
                    <Col xs={24} sm={12} lg={8}>
                        <Card>
                            <Statistic
                                title={t('totalDrugsPrescribed')}
                                value={overviewData?.totalDrugsPrescribed || 0}
                                prefix={<BarChartOutlined />}
                                valueStyle={{ color: '#13c2c2' }}
                            />
                        </Card>
                    </Col>
                </Row>
            </Card>

            <Divider />

            {/* Doctor-Specific Reports */}
            <Card title={t('doctorReports')} className="doctor-reports-card">
                <Row gutter={[16, 16]} style={{ marginBottom: 24 }}>
                    <Col xs={24} md={12}>
                        <Select
                            style={{ width: '100%' }}
                            placeholder={t('selectDoctor')}
                            onChange={handleDoctorChange}
                            value={selectedDoctor}
                            allowClear
                            showSearch
                            filterOption={(input, option) =>
                                option.children.toLowerCase().includes(input.toLowerCase())
                            }
                        >
                            {doctors.map(doctor => (
                                <Option key={doctor._id} value={doctor._id}>
                                    {doctor.name} - {doctor.specialization}
                                </Option>
                            ))}
                        </Select>
                    </Col>
                    <Col xs={24} md={12}>
                        <RangePicker
                            style={{ width: '100%' }}
                            onChange={setDateRange}
                            format="YYYY-MM-DD"
                        />
                    </Col>
                </Row>

                {doctorReport && (
                    <>
                        {/* Doctor Info */}
                        <Card size="small" style={{ marginBottom: 16 }}>
                            <Row gutter={[16, 16]}>
                                <Col span={24}>
                                    <h3>{doctorReport.doctor?.name}</h3>
                                    <Space>
                                        <Tag color="blue">{doctorReport.doctor?.specialization}</Tag>
                                        <Tag color={doctorReport.doctor?.isActive ? 'green' : 'red'}>
                                            {doctorReport.doctor?.isActive ? t('active') : t('inactive')}
                                        </Tag>
                                    </Space>
                                    <p style={{ marginTop: 8 }}>
                                        {t('email')}: {doctorReport.doctor?.email}<br />
                                        {t('phoneNumber')}: {doctorReport.doctor?.phoneNumber}
                                    </p>
                                    {doctorReport.doctor?.createdBy && (
                                        <p>
                                            {t('createdBy')}: {doctorReport.doctor.createdBy.name}
                                            ({doctorReport.doctor.createdBy.companyName || t(doctorReport.doctor.createdBy.role)})
                                        </p>
                                    )}
                                </Col>
                            </Row>
                        </Card>

                        {/* Doctor Statistics */}
                        <Row gutter={[16, 16]} style={{ marginBottom: 16 }}>
                            <Col xs={12} sm={6}>
                                <Card>
                                    <Statistic
                                        title={t('patients')}
                                        value={doctorReport.statistics?.totalPatients || 0}
                                        prefix={<UserOutlined />}
                                    />
                                </Card>
                            </Col>
                            <Col xs={12} sm={6}>
                                <Card>
                                    <Statistic
                                        title={t('visits')}
                                        value={doctorReport.statistics?.totalVisits || 0}
                                        prefix={<FileTextOutlined />}
                                    />
                                </Card>
                            </Col>
                            <Col xs={12} sm={6}>
                                <Card>
                                    <Statistic
                                        title={t('secretaries')}
                                        value={doctorReport.statistics?.totalSecretaries || 0}
                                        prefix={<TeamOutlined />}
                                    />
                                </Card>
                            </Col>
                            <Col xs={12} sm={6}>
                                <Card>
                                    <Statistic
                                        title={t('drugsPrescribed')}
                                        value={doctorReport.statistics?.totalDrugsPrescribed || 0}
                                        prefix={<MedicineBoxOutlined />}
                                    />
                                </Card>
                            </Col>
                        </Row>

                        {/* Top Prescribed Drugs */}
                        {doctorReport.topPrescribedDrugs && doctorReport.topPrescribedDrugs.length > 0 && (
                            <Card title={t('topPrescribedDrugs')} style={{ marginBottom: 16 }}>
                                <Table
                                    columns={topDrugsColumns}
                                    dataSource={doctorReport.topPrescribedDrugs}
                                    rowKey="name"
                                    pagination={{ pageSize: 10 }}
                                    size="small"
                                />
                            </Card>
                        )}

                        {/* Patients List */}
                        {doctorReport.patients && doctorReport.patients.length > 0 && (
                            <Card title={t('patients')} style={{ marginBottom: 16 }}>
                                <Table
                                    columns={patientsColumns}
                                    dataSource={doctorReport.patients}
                                    rowKey="_id"
                                    pagination={{ pageSize: 10 }}
                                    size="small"
                                />
                            </Card>
                        )}

                        {/* Recent Visits */}
                        {doctorReport.recentVisits && doctorReport.recentVisits.length > 0 && (
                            <Card title={t('recentVisits')}>
                                <Table
                                    columns={recentVisitsColumns}
                                    dataSource={doctorReport.recentVisits}
                                    rowKey="_id"
                                    pagination={{ pageSize: 10 }}
                                    size="small"
                                />
                            </Card>
                        )}
                    </>
                )}

                {!selectedDoctor && (
                    <div style={{ textAlign: 'center', padding: '40px 0', color: '#999' }}>
                        <BarChartOutlined style={{ fontSize: 48, marginBottom: 16 }} />
                        <p>{t('selectDoctorToViewReport') || 'Please select a doctor to view detailed reports'}</p>
                    </div>
                )}
            </Card>
        </div>
    );
};

export default SuperAdminReports;
